import { Logout } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AlertDefault, { AlertState, AlertVariant } from "~/src/components/Alerts/AlertDefault";
import AlertWrapper from "~/src/components/Alerts/AlertWrapper";
import BottomLine from "~/src/components/BottomLine";
import SoSCard from "~/src/components/Card/SoSCard";
import CountCircle from "~/src/components/CountCirle";
import { AddButtonIcon, CancelIcon, EditIcon, ProfileIcon } from "~/src/components/Icons/Icons";
import Text, { Header, TextVariant } from "~/src/components/Text/Text";
import { StorageContext, StorageItem } from "~/src/contexts/StorageContext/StorageProvider";
import UIContext from "~/src/contexts/UIContext/UIProvider";
import { useConfirmationDialog } from "~/src/hooks/useConfirmationDialog";
import { useDialog } from "~/src/hooks/useDialog";
import { useReload } from "~/src/hooks/useReload";
import { useStateful } from "~/src/hooks/useStateful";
import { getEnvironmentLabel } from "~/src/appConfig";
import { Favorittområde, Individ, Jaktdag, Område } from "../../../api/types";
import ButtonDefault, {
  ButtonLayout,
  ButtonVariant,
} from "../../../components/Button/ButtonDefault";
import JaktområdeDefault from "../../../components/Jaktomrade/Jaktomrade";
import RenderQueryResult from "../../../components/RenderQuery/RenderQueryResult";
import AppDataContext from "../../../contexts/AppDataContext/DataProvider";
import AuthContext from "../../../contexts/AuthContext/AuthProvider";
import Lenker from "./Lenker";

export default function (): JSX.Element {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appData = useContext(AppDataContext);
  const storage = useContext(StorageContext);
  const jegeropplysninger = appData.jegeropplysninger;
  const showAddFavoritesAlert = useStateful<boolean>(false);
  const ui = useContext(UIContext);
  const reload = useReload("Minside");
  const appName = "settogskutt";
  const devTimestamp = new Date(import.meta.env.VITE_BUILD_TIMESTAMP).toLocaleDateString("nb-NO", {
    timeZone: "Europe/Oslo",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const sortedJaktomrader = useMemo(() => {
    const data = appData.jaktområder.data;
    if (!data) return undefined;
    return [...data].sort((a, b) => {
      if (a?.SistOppdatert && b?.SistOppdatert) {
        return a.SistOppdatert <= b.SistOppdatert ? 1 : -1;
      }
      return 0;
    });
  }, [appData.jaktområder.data]);

  const handleLogout = () => {
    auth.handleLogout();
    queryClient.clear();
    navigate("/");
  };

  const deleteData = async () => {
    await caches.keys().then((keys) => {
      Promise.all(
        keys.map((key) => {
          if (key.startsWith(appName)) {
            caches.delete(key);
          }
        }),
      ).then(() => {
        localStorage.clear();
        sessionStorage.clear();
      });
    });
    return navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
  };

  const confirmDeleteAppDataDialog = useConfirmationDialog({
    title: t("pages.logout.clearDataAndReload"),
    message: t("pages.logout.clearDataAndReloadMessage"),
    confirmButtonText: t("actions.yes"),
    onConfirm: async () => deleteData().then(() => reload.reload()),
    cancelButtonText: t("actions.no"),
    buttonLayout: ButtonLayout.HorizontalReverse,
  });

  const logoutDialog = useDialog({
    title: t("pages.logout.confirm"),
    isBottomModal: true,
    buttons: (
      <>
        <ButtonDefault
          leftAdjusted
          id="confirm"
          onClick={handleLogout}
          label={t("pages.logout.confirmButton")}
          icon={<Logout />}
        />
        <ButtonDefault
          leftAdjusted
          id="tøm"
          label={t("pages.logout.clearDataAndReload")}
          variant={ButtonVariant.Secondary}
          icon={
            <svg
              role="graphics-symbol"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="16"
              viewBox="0 0 20 16"
              fill="none"
            >
              <path
                d="M12.5 13H14.5C14.9167 13 15.2708 12.8542 15.5625 12.5625C15.8542 12.2708 16 11.9167 16 11.5V7.5H17V6H14.5V5H12.5V6H10V7.5H11V11.5C11 11.9167 11.1458 12.2708 11.4375 12.5625C11.7292 12.8542 12.0833 13 12.5 13ZM12.5 7.5H14.5V11.5H12.5V7.5ZM2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H8L10 2H18C18.55 2 19.0208 2.19583 19.4125 2.5875C19.8042 2.97917 20 3.45 20 4V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16H2ZM2 14H18V4H9.175L7.175 2H2V14Z"
                fill="#007B97"
              />
            </svg>
          }
          onClick={() => confirmDeleteAppDataDialog.show()}
        />
        <ButtonDefault
          leftAdjusted
          id="cancel"
          label={t("actions.cancel")}
          variant={ButtonVariant.Secondary}
          icon={<CancelIcon />}
          onClick={() => {
            logoutDialog.close();
          }}
        />
      </>
    ),
  });

  useEffect(() => {
    showAddFavoritesAlert.set(
      storage?.get<AlertState>({ name: "InfoAlert", id: "addFavoritesInfo" })?.data.show ?? true,
    );
  }, []);

  const områdeEntry = (områdeEntry: Favorittområde | Område) => {
    let _områdeEntry = områdeEntry;
    return (
      <JaktområdeDefault
        isLink={true}
        områdeEntry={_områdeEntry}
        selectable={false}
        onClick={async () => {
          navigate("/minside/jaktomrade", { state: { jaktomrade: områdeEntry } });
        }}
      />
    );
  };

  const globalNotifications = ui.getGlobalNotifications();

  return (
    <div className="mx-4 flex flex-col justify-between h-full min-h-fit">
      <div>
        <AlertWrapper>
          {globalNotifications.length != 0 && (
            <div className="flex flex-col p-2 mt-4 gap-px">
              {globalNotifications.map((notification) => (
                <AlertDefault variant={AlertVariant.Warning} key={notification.message}>
                  {notification.message}
                </AlertDefault>
              ))}
            </div>
          )}
        </AlertWrapper>

        <RenderQueryResult query={jegeropplysninger} queryKey="jegeropplysninger">
          <SoSCard
            className="my-4"
            titleString={`${jegeropplysninger.data?.Jegernummer} (${t(
              "form.fields.jegernummer.label",
            )})`}
            titleIcon={<ProfileIcon className="text-ldir-blue-900" />}
            titleButton={
              <IconButton
                onClick={async () => navigate("/minside/jegeropplysninger")}
                className="bg-ldir-blue-800 rounded-xl p-2.25 text-ldir-blue-100 focus-visible:outline-4 outline-ldir-blue-600"
              >
                <EditIcon title={t("actions.edit")} />
              </IconButton>
            }
            cardFields={[
              {
                label: t("pages.minSide.mineOpplysninger.navn"),
                value: jegeropplysninger.data?.Navn,
              },
              {
                label: t("pages.minSide.mineOpplysninger.telefon"),
                value: jegeropplysninger.data?.Telefonnummer,
              },
              {
                label: t("pages.minSide.mineOpplysninger.epost"),
                value:
                  jegeropplysninger.data?.Epost != "" ? (
                    jegeropplysninger.data?.Epost
                  ) : (
                    <Text variant={TextVariant.large} className="text-[#37261D99]">
                      adress@mail.no
                    </Text>
                  ),
              },
            ]}
          >
            <></>
          </SoSCard>
        </RenderQueryResult>
        <div className="flex flex-row justify-between items-end mb-2 pt-2">
          <div className="flex gap-2 items-center">
            <Header headerLvl={2} variant={TextVariant.small} className="font-normal">
              {t("pages.minSide.mineJaktområder.title")}
            </Header>
            <CountCircle count={sortedJaktomrader?.length} />
          </div>
          <div className="flex flex-row items-center col-span-1">
            <IconButton
              onClick={async () => navigate("/minside/jaktomrader")}
              className="p-0 focus-visible:outline-4 outline-ldir-blue-600"
            >
              <AddButtonIcon title={t("pages.jaktområder.title")} />
            </IconButton>
          </div>
        </div>
        <RenderQueryResult
          query={appData.jaktområder}
          queryKey="jaktomrader"
          key={appData.jaktområder.data?.length}
        >
          <div className="w-full overflow-y-auto">
            {sortedJaktomrader && sortedJaktomrader.length > 0 ? (
              <>
                {sortedJaktomrader.map((o, i) => (
                  <div key={`${o.Områdekode}-${i}`}>{områdeEntry(o)}</div>
                ))}
                <BottomLine />
              </>
            ) : (
              <>
                <BottomLine />
                <AlertDefault variant={AlertVariant.Warning}>
                  {t("pages.minSide.mineJaktområder.leggTilOmrader")}
                </AlertDefault>
              </>
            )}
          </div>
        </RenderQueryResult>
        <Lenker />
      </div>

      <div className="text-center my-10 mx-2">
        <ButtonDefault
          id="Logout"
          label={t("actions.logout")}
          variant={ButtonVariant.Primary}
          icon={<Logout />}
          onClick={() => logoutDialog.show()}
        />
      </div>
      {logoutDialog.element}
      {confirmDeleteAppDataDialog.element}
      {getEnvironmentLabel() !== undefined && (
        <div className="p-2 mb-2 bg-ldir-blue-500 rounded">
          <p className="text-xl font-bold">Debug-informasjon</p>
          <p>Bygget: {devTimestamp}</p>
          <BottomLine />

          <p className="mt-2">Pending: {storage.anyPending() ? "Ja" : "Nei"}</p>
          <p className="text-lg font-bold mt-2">
            Lokal kø: {storage.getAll().filter((i) => i.objectType != "Other").length}
          </p>
          <ul>
            {storage
              .getAll()
              .filter((i) => i.objectType != "Other")
              .map((o, i) => {
                return (
                  <li className={`m-2 ${o.operation === "none" ? "opacity-60" : ""}`} key={i}>
                    <p className="mr-1 font-bold">{o.objectType}:</p>
                    {o.objectType === "Jaktdag" ? (
                      <>
                        <p className="font-semibold">
                          {(o.data as Jaktdag).Art} - {(o.data as Jaktdag).Jaktfelt} -{" "}
                          {(o.data as Jaktdag).Dato.split("T")[0]}
                        </p>
                        <p>operation: {(o as StorageItem<Jaktdag>).operation}</p>
                        <p>syncStatus: {(o as StorageItem<Jaktdag>).syncStatus}</p>
                        <p>isPending: {(o as StorageItem<Jaktdag>).isPending ? "true" : "false"}</p>
                        <p>{(o as StorageItem<Jaktdag>).errors?.Individer}</p>
                        <p>{(o as StorageItem<Jaktdag>).errors?.Id}</p>
                      </>
                    ) : o.objectType === "Individ" ? (
                      <>
                        <p className="font-semibold">
                          {(o.data as Individ).Art} ({(o.data as Individ).Kjønn}{" "}
                          {(o.data as Individ).Alder}) {(o.data as Individ).Dato.split("T")[0]}
                        </p>
                        <p>
                          Jaktområde: {(o.data as Individ).Jaktfelt} {(o.data as Individ).Vald}{" "}
                          {(o.data as Individ).Villreinvald}
                        </p>
                        <p>operation: {(o as StorageItem<Individ>).operation}</p>
                        <p>syncStatus: {(o as StorageItem<Individ>).syncStatus}</p>
                        <p>isPending: {(o as StorageItem<Individ>).isPending ? "true" : "false"}</p>
                        <p>{(o as StorageItem<Individ>).errors?.Id}</p>
                      </>
                    ) : (
                      o.objectType === "PendingJaktomrade" && (
                        <>
                          <p className="font-semibold">
                            {(o.data as Favorittområde).Områdekode} -{" "}
                            {(o.data as Favorittområde).Områdenavn}
                          </p>
                          <p>operation: {(o as StorageItem<Favorittområde>).operation}</p>
                          <p>syncStatus: {(o as StorageItem<Favorittområde>).syncStatus}</p>
                          <p>
                            isPending:{" "}
                            {(o as StorageItem<Favorittområde>).isPending ? "true" : "false"}
                          </p>
                        </>
                      )
                    )}
                  </li>
                );
              })}
            <li>
              <ButtonDefault
                id={"Fjern kø"}
                label="Tøm lokal kø"
                onClick={() => {
                  storage
                    .getAll<Jaktdag>("Jaktdag")
                    .forEach((j) => storage.delete(j.objectKey, "Jaktdag"));
                  storage
                    .getAll<Individ>("Individ")
                    .forEach((i) => storage.delete(i.objectKey, "Individ"));
                  storage
                    .getAll<Favorittområde>("PendingJaktomrade")
                    .forEach((f) => storage.delete(f.objectKey, "PendingJaktomrade"));
                  reload.reload();
                }}
              />
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
